import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress } from '@mui/material';
import DialogActions, {
  type DialogActionsProps
} from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { type MouseEventHandler } from 'react';

type ButtonColor =
  | 'primary'
  | 'inherit'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning';

export type ButtonProps = {
  text: string;
  textColor?: string;
  color?: ButtonColor;
  variant?: 'text' | 'outlined' | 'contained';
  onClick: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  disabled?: boolean;
  startIcon?: React.ReactNode | string;
};

type Props = DialogActionsProps & {
  primaryButton: ButtonProps;
  secondaryButton?: ButtonProps;
  tertiaryButton?: ButtonProps;
};

const Text: React.FC<{ text: string; color?: string }> = ({ text, color }) => (
  <Typography
    component="span"
    sx={{ color }}
    textTransform={'none'}
    fontWeight={500}
    fontSize={14}
  >
    {text}
  </Typography>
);

const Footer: React.FC<Props> = ({
  primaryButton,
  secondaryButton,
  tertiaryButton,
  ...props
}) => {
  return (
    <DialogActions
      {...props}
      sx={{
        padding: '12px',
        height: '56px',
        justifyContent: !tertiaryButton ? 'flex-end' : 'space-between',
        ...props.sx
      }}
    >
      {tertiaryButton && (
        <LoadingButton
          hidden={true}
          variant={tertiaryButton.variant || 'text'}
          onClick={tertiaryButton.onClick}
          disabled={tertiaryButton.disabled}
          loading={tertiaryButton.loading}
          color={tertiaryButton.color}
          startIcon={tertiaryButton.startIcon}
          size="medium"
        >
          {tertiaryButton.loading ? (
            <CircularProgress size={24} sx={{ color: '#333' }} />
          ) : (
            <Text
              color={
                tertiaryButton.disabled ? 'rgba(0, 0, 0, 0.38)' : '#01677C'
              }
              text={tertiaryButton.text}
            />
          )}
        </LoadingButton>
      )}
      <Box>
        {secondaryButton && (
          <LoadingButton
            variant={secondaryButton.variant || 'text'}
            onClick={secondaryButton.onClick}
            disabled={secondaryButton.disabled}
            loading={secondaryButton.loading}
            color={secondaryButton.color}
            startIcon={secondaryButton.startIcon}
            style={{ marginRight: 8 }}
            size="medium"
          >
            {secondaryButton.loading ? (
              <CircularProgress size={24} sx={{ color: '#333' }} />
            ) : (
              <Text
                color={
                  secondaryButton.disabled ? 'rgba(0, 0, 0, 0.38)' : '#01677C'
                }
                text={secondaryButton.text}
              />
            )}
          </LoadingButton>
        )}

        <LoadingButton
          variant={primaryButton.variant || 'contained'}
          onClick={primaryButton.onClick}
          disabled={primaryButton.disabled}
          loading={primaryButton.loading}
          color={primaryButton.color || 'primary'}
          startIcon={primaryButton.startIcon}
          style={{ marginRight: 8 }}
          size="medium"
        >
          {primaryButton.loading ? (
            <CircularProgress size={24} sx={{ color: 'rgba(0, 0, 0, 0.38)' }} />
          ) : (
            <Text
              color={primaryButton.disabled ? 'rgba(0, 0, 0, 0.38)' : 'white'}
              text={primaryButton.text}
            />
          )}
        </LoadingButton>
      </Box>
    </DialogActions>
  );
};

export default Footer;
